import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import { Course } from '@/client/courses';
import { LazyLoadingMultiselect } from '@/components/form/generic';
import React, { HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CoursesMultiselectInputProps = {
  onChange: (value: Course[]) => void;
  accountId: string;
  isSystemAccount: boolean;
  additionalFilters?: FiltersType;
  selectedOptions?: Course[];
  getUnavailableCourses?: boolean;
  isDisabled?: boolean;
  filterTemplate?: React.ReactElement;
  panelClassName?: string;
  placeholder?: string;
  filterPlaceholder?: string;
  isSearchable?: boolean;
} & Omit<HTMLAttributes<HTMLSelectElement>, 'onChange'>;

export const CoursesMultiselectInput: React.FC<
  CoursesMultiselectInputProps
> = ({
  onChange,
  accountId,
  isSystemAccount,
  className,
  additionalFilters,
  selectedOptions,
  isDisabled,
  getUnavailableCourses,
  filterTemplate,
  panelClassName,
  placeholder,
  filterPlaceholder,
  isSearchable = false,
}) => {
  const { t } = useTranslation();
  const [refetchKey] = useState<number>(0);
  const [selectedCourses, setSelectedCourses] = useState<Course[]>(
    selectedOptions || [],
  );

  useEffect(() => {
    setSelectedCourses(selectedOptions || []);
  }, [selectedOptions]);

  const fetchCourses = isDisabled
    ? undefined
    : getUnavailableCourses
    ? async (params: ListingRequest) =>
        await client.courses.getUnavailableCoursesToAccount(params, accountId)
    : async (params: ListingRequest) =>
        isSystemAccount
          ? await client.courses.getCourses(params)
          : await client.courses.getAccountCourses(params, accountId);

  return (
    <LazyLoadingMultiselect
      fetchService={fetchCourses}
      selectedValues={selectedCourses}
      handleOnChange={onChange}
      filter
      filterPlaceholder={filterPlaceholder}
      optionLabel="name"
      dataKey="id"
      placeholder={placeholder ?? t('courses.select')}
      maxSelectedItems={2}
      additionalFilters={additionalFilters}
      className={className}
      isDisabled={isDisabled}
      filterTemplateAddition={filterTemplate}
      panelClassName={panelClassName}
      refetchKey={refetchKey}
      isSearchable={isSearchable}
    />
  );
};
